import { DynamicStandaloneArticleDate, DynamicStandaloneArticleDateProps } from '@/components/ArticleDate';
import { DynamicStandaloneByline, DynamicStandaloneBylineProps } from '@/components/Byline';
import { DynamicStandaloneImage, DynamicStandaloneImageProps } from '@/components/Image';
import { DynamicStandaloneTag, DynamicStandaloneTagProps } from '@/components/Tag';
import { useStableProps } from '@/hooks/useStable';
import { ClassNameProvider, componentTheme } from '@/styles/ArticleHeader';
import { StandaloneComponent, StandaloneComponentProps } from '@/types/component';
import { Content } from '@/types/content';
import { mergeOptions, mergeProps } from '@/utils/merge';
import { ReactNode, useMemo } from 'react';
import { ArticleHeader, ArticleHeaderSubcomponentProps } from './ArticleHeader';

export const findBylines = (descendants: Content[]) => {
  const foundBylines: Content[] = [];

  const findBylinesRecursive = (descendants: Content[]) =>
    descendants.map((descendant) => {
      if (descendant.type === 'byline') {
        foundBylines.push(descendant);
      }
      if (descendant.children.length > 0) {
        findBylinesRecursive(descendant.children);
      }
    });
  findBylinesRecursive(descendants);

  return foundBylines;
};

interface ExtendedOptions {
  $category?: ArticleHeaderSubcomponentProps['Category'] & {
    $tag?: DynamicStandaloneTagProps['options'];
  };
  $tags?: ArticleHeaderSubcomponentProps['Tags'] & {
    $tag?: DynamicStandaloneTagProps['options'];
  };
}

export interface StandaloneArticleHeaderProps extends StandaloneComponentProps<typeof ArticleHeader, ExtendedOptions> {
  bylines?: DynamicStandaloneBylineProps[];
  category?: {
    value?: ReactNode;
    url?: string;
  };
  description?: ReactNode;
  headline?: ReactNode;
  image?: DynamicStandaloneImageProps;
  tags?: DynamicStandaloneTagProps[];
  datetime?: DynamicStandaloneArticleDateProps;
}

export const StandaloneArticleHeader: StandaloneComponent<StandaloneArticleHeaderProps> = ({
  bylines,
  category,
  description,
  headline,
  image,
  tags,
  datetime,
  options,
  ...props
}) => {
  const {
    $bylines,
    $date,
    $category: categoryProps,
    $description,
    $headline,
    $image,
    $tags: tagsProps,
    ...$base
  } = options ?? {};

  const { $tag: $categoryTag, ...$category } = categoryProps ?? {};
  const { $tag, ...$tags } = tagsProps ?? {};

  const memoTags = useMemo(
    () =>
      tags?.map((tag, index) => (
        <DynamicStandaloneTag
          key={index}
          link={{ href: `/etikett/${tag.value}` }}
          {...mergeProps(
            {
              options: {
                variant: 'text',
                theme: componentTheme('tag'),
                ...$tag,
              },
            },
            tag
          )}
        />
      )),
    [tags, $tag]
  );

  const memoCategoryTag = useMemo(
    () => (
      <DynamicStandaloneTag
        value={category?.value}
        link={{ href: category?.url || `etikett/${category?.value}` }}
        options={mergeOptions(
          {
            variant: 'text',
            theme: componentTheme('tag'),
          },
          $categoryTag
        )}
      />
    ),
    [$categoryTag, category?.url, category?.value]
  );

  const stableImageProps = useStableProps({ fill: true, priority: true }, image);

  return (
    <ClassNameProvider value={{ size: 'default', colors: 'default', ...$base }}>
      <ArticleHeader {...$base} {...props}>
        {tags && tags.length > 0 && <ArticleHeader.Tags {...$tags}>{memoTags}</ArticleHeader.Tags>}
        {headline && <ArticleHeader.Headline {...$headline}>{headline}</ArticleHeader.Headline>}
        {category?.value && <ArticleHeader.Category {...$category}>{memoCategoryTag}</ArticleHeader.Category>}
        {datetime?.publishedDatetime && (
          <ArticleHeader.Date {...$date}>
            <DynamicStandaloneArticleDate {...datetime} />
          </ArticleHeader.Date>
        )}
        {bylines && bylines?.length > 0 && (
          <ArticleHeader.Bylines {...$bylines}>
            {bylines?.map((byline, index) => (
              <DynamicStandaloneByline {...byline} key={index} />
            ))}
          </ArticleHeader.Bylines>
        )}
        {image && (
          <ArticleHeader.Image {...$image}>
            <DynamicStandaloneImage {...stableImageProps} />
          </ArticleHeader.Image>
        )}
        {description && <ArticleHeader.Description {...$description}>{description}</ArticleHeader.Description>}
      </ArticleHeader>
    </ClassNameProvider>
  );
};

export default StandaloneArticleHeader;
